import { useEffect, useRef, useState } from "react";

const IDLE_MS = 9 * 60 * 1000;
const GRACE_MS = 60 * 1000;
export const SESSION_ACTIVITY_KEY = "visaro.session.activity";

/** Browser inactivity only. Staying here does not renew an expired API token. */
export default function IdleSessionWarning({ userId, logout }: {
  userId: string;
  logout: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [fallback, setFallback] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const continueSession = useRef<() => void>(() => {});
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) return;
    // Non-secret session tag, only for separating idle timestamps after login.
    // This is not used to authenticate the customer.
    let sessionHash = 0;
    for (let i = 0; i < token.length; i++) sessionHash = Math.imul(31, sessionHash) + token.charCodeAt(i) | 0;
    const session = `${userId}:${sessionHash}`;
    let lastActivity = Date.now();
    let stopped = false;
    const readActivity = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(SESSION_ACTIVITY_KEY) || "null");
        if (saved?.session === session && Number.isFinite(saved.at) && saved.at <= Date.now()) {
          lastActivity = Math.max(lastActivity, saved.at);
        }
      } catch { /* Keep the in-memory deadline if storage is unavailable. */ }
    };
    const writeActivity = () => {
      lastActivity = Date.now();
      try {
        localStorage.setItem(SESSION_ACTIVITY_KEY, JSON.stringify({ session, at: lastActivity }));
      } catch { /* The timer still works within this tab. */ }
    };
    // Preserve the deadline through refreshes and across tabs.
    try {
      const saved = JSON.parse(localStorage.getItem(SESSION_ACTIVITY_KEY) || "null");
      if (saved?.session === session && Number.isFinite(saved.at) && saved.at <= Date.now()) {
        lastActivity = saved.at;
      } else writeActivity();
    } catch { writeActivity(); }

    const check = () => {
      if (stopped) return;
      if (localStorage.getItem("token") !== token) {
        // A different tab signed out or switched accounts. Discard this tab's profile.
        stopped = true;
        window.location.reload();
        return;
      }
      readActivity();
      const remaining = lastActivity + IDLE_MS + GRACE_MS - Date.now();
      if (remaining <= 0) {
        stopped = true;
        logout();
      } else {
        setSecondsLeft(remaining <= GRACE_MS ? Math.ceil(remaining / 1000) : null);
      }
    };
    const activity = () => {
      if (stopped || localStorage.getItem("token") !== token) return check();
      readActivity();
      // Once the warning is due, require an explicit answer, not a stray mouse move.
      if (Date.now() - lastActivity >= IDLE_MS) return check();
      if (Date.now() - lastActivity >= 1000) writeActivity();
    };
    continueSession.current = () => {
      check();
      if (stopped) return;
      writeActivity();
      setSecondsLeft(null);
    };
    const events = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    events.forEach(event => window.addEventListener(event, activity, { passive: true }));
    window.addEventListener("storage", check);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    const interval = window.setInterval(check, 1000);
    check();
    return () => {
      stopped = true;
      window.clearInterval(interval);
      events.forEach(event => window.removeEventListener(event, activity));
      window.removeEventListener("storage", check);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [userId, logout, token]);

  const open = secondsLeft !== null;
  useEffect(() => {
    const element = dialog.current;
    if (open && element && !element.open) {
      try { element.showModal(); } catch { setFallback(true); }
    }
    if (!open && element?.open) {
      if (typeof element.close === "function") element.close();
      else element.removeAttribute("open");
    }
  }, [open]);

  useEffect(() => {
    if (fallback && open) dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [fallback, open]);

  return (
    <>
    {fallback && open && <div aria-hidden="true" className="fixed inset-0 z-[9999] bg-black/50" />}
    <dialog ref={dialog} open={fallback ? open : undefined} aria-modal="true"
      aria-labelledby="idle-session-title" aria-describedby="idle-session-description"
      onCancel={event => event.preventDefault()}
      onKeyDown={event => {
        if (!fallback || event.key !== "Tab") return;
        const buttons = dialog.current?.querySelectorAll<HTMLButtonElement>("button");
        if (!buttons?.length) return;
        event.preventDefault();
        const next = document.activeElement === buttons[0] ? buttons[1] : buttons[0];
        next.focus();
      }}
      className={`w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white p-6 text-[#1B1933] backdrop:bg-black/50 ${!open ? "hidden" : ""} ${fallback ? "fixed inset-0 z-[10000] m-auto h-fit shadow-2xl" : ""}`}>
      <h2 id="idle-session-title" className="text-xl font-semibold">Are you still here?</h2>
      <p id="idle-session-description" className="mt-3 text-sm">
        You have been inactive for 9 minutes. We will sign you out in {secondsLeft ?? 60} seconds unless you choose to stay signed in.
      </p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button autoFocus type="button" onClick={() => continueSession.current()}
          className="rounded-lg bg-[#F15A24] px-5 py-3 font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
          Yes, stay signed in
        </button>
        <button type="button" onClick={logout} className="rounded-lg border px-5 py-3 font-semibold">
          Sign out
        </button>
      </div>
    </dialog>
    </>
  );
}
